import { useEffect, useState, useMemo } from 'react';
import financasApi from '@/lib/financasApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Pencil, Trash2, Search, Users, MapPin, Building2, Phone, Mail, FileText, User } from 'lucide-react';
import { toast } from 'sonner';

const UFS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

const formVazio = () => ({
  tipo_pessoa: 'fisica',
  nome: '',
  nome_fantasia: '',
  cpf_cnpj: '',
  rg: '',
  inscricao_estadual: '',
  inscricao_municipal: '',
  indicador_ie: '9',
  telefone: '',
  email: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  codigo_ibge: '',
  pais: 'Brasil',
  observacao: '',
});

// formatadores básicos
const maskCEP = (v) => v.replace(/\D/g, '').replace(/^(\d{5})(\d)/, '$1-$2').slice(0, 9);
const maskCPF = (v) => v.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2').slice(0, 14);
const maskCNPJ = (v) => v.replace(/\D/g, '').replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2').slice(0, 18);
const maskFone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
};

export default function FinancasClientes() {
  const [items, setItems] = useState([]);
  const [busca, setBusca] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(formVazio());
  const [loadingCep, setLoadingCep] = useState(false);

  const load = async () => {
    const { data } = await financasApi.get('/financas/clientes');
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!busca) return items;
    const b = busca.toLowerCase();
    return items.filter(c =>
      (c.nome || '').toLowerCase().includes(b) ||
      (c.nome_fantasia || '').toLowerCase().includes(b) ||
      (c.cpf_cnpj || '').toLowerCase().includes(b) ||
      (c.telefone || '').includes(busca) ||
      (c.email || '').toLowerCase().includes(b)
    );
  }, [items, busca]);

  const openNovo = () => { setEditing(null); setForm(formVazio()); setOpen(true); };
  const openEditar = (c) => {
    setEditing(c);
    setForm({ ...formVazio(), ...c });
    setOpen(true);
  };

  const buscarCep = async () => {
    const cep = (form.cep || '').replace(/\D/g, '');
    if (cep.length !== 8) { toast.error('CEP deve ter 8 dígitos'); return; }
    setLoadingCep(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await r.json();
      if (data.erro) { toast.error('CEP não encontrado'); return; }
      setForm({
        ...form,
        logradouro: data.logradouro || form.logradouro,
        bairro: data.bairro || form.bairro,
        cidade: data.localidade || form.cidade,
        uf: data.uf || form.uf,
        codigo_ibge: data.ibge || form.codigo_ibge,
      });
      toast.success('Endereço preenchido');
    } catch {
      toast.error('Falha ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { toast.error('Nome obrigatório'); return; }
    const payload = { ...form, nome: form.nome.trim() };
    // Limpar strings vazias para null
    Object.keys(payload).forEach(k => {
      if (payload[k] === '') payload[k] = null;
    });
    payload.nome = form.nome.trim();
    payload.tipo_pessoa = form.tipo_pessoa || 'fisica';
    payload.indicador_ie = form.indicador_ie || '9';
    payload.pais = form.pais || 'Brasil';
    try {
      if (editing) {
        await financasApi.put(`/financas/clientes/${editing.id}`, payload);
        toast.success('Cliente atualizado');
      } else {
        await financasApi.post('/financas/clientes', payload);
        toast.success('Cliente cadastrado');
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const excluir = async (c) => {
    if (!window.confirm(`Excluir o cliente "${c.nome}"?`)) return;
    await financasApi.delete(`/financas/clientes/${c.id}`);
    toast.success('Cliente removido');
    load();
  };

  const isPJ = form.tipo_pessoa === 'juridica';

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="financas-clientes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3"><Users className="w-6 h-6 md:w-7 md:h-7 text-emerald-400" /> Clientes</h1>
          <p className="text-slate-400 text-xs md:text-sm mt-1">Cadastro completo (preparado para emissão de NF-e)</p>
        </div>
        <Button onClick={openNovo} data-testid="financas-cliente-novo-btn" className="bg-emerald-600 hover:bg-emerald-500 text-white">
          <Plus className="w-4 h-4 mr-2" /> Novo Cliente
        </Button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input data-testid="financas-clientes-busca" placeholder="Buscar por nome, CPF/CNPJ, telefone ou e-mail..." value={busca} onChange={(e) => setBusca(e.target.value)} className="bg-slate-900 border-white/10 text-white pl-10" />
      </div>

      {/* Cards mobile */}
      <div className="md:hidden space-y-2">
        {filtered.length === 0 ? (
          <div className="bg-slate-900 border border-white/10 rounded-xl py-12 text-center text-slate-500">Nenhum cliente cadastrado</div>
        ) : filtered.map(c => (
          <div key={c.id} className="bg-slate-900 border border-white/10 rounded-xl p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-white font-medium text-sm">
                  {c.tipo_pessoa === 'juridica' ? <Building2 className="w-3.5 h-3.5 text-emerald-400" /> : <User className="w-3.5 h-3.5 text-emerald-400" />}
                  <span className="truncate">{c.nome}</span>
                </div>
                {c.nome_fantasia && <p className="text-xs text-slate-400 truncate">{c.nome_fantasia}</p>}
                <p className="text-xs text-slate-500 mt-1">{c.cpf_cnpj || 'sem doc'}</p>
                {c.telefone && <p className="text-xs text-slate-400 flex items-center gap-1 mt-1"><Phone className="w-3 h-3" />{c.telefone}</p>}
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEditar(c)} className="text-slate-400 h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => excluir(c)} className="text-red-500 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabela desktop */}
      <div className="hidden md:block bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/50">
            <tr className="text-left text-slate-400 text-xs uppercase tracking-wider">
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Nome / Razão Social</th>
              <th className="px-4 py-3">CPF / CNPJ</th>
              <th className="px-4 py-3">Telefone</th>
              <th className="px-4 py-3">Cidade/UF</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">Nenhum cliente cadastrado</td></tr>
            ) : filtered.map(c => (
              <tr key={c.id} className="hover:bg-white/5">
                <td className="px-4 py-3">
                  {c.tipo_pessoa === 'juridica' ? (
                    <span className="text-xs bg-blue-500/10 text-blue-300 border border-blue-500/30 px-2 py-1 rounded-full inline-flex items-center gap-1"><Building2 className="w-3 h-3" />PJ</span>
                  ) : (
                    <span className="text-xs bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 px-2 py-1 rounded-full inline-flex items-center gap-1"><User className="w-3 h-3" />PF</span>
                  )}
                </td>
                <td className="px-4 py-3 text-white">
                  <div>{c.nome}</div>
                  {c.nome_fantasia && <div className="text-xs text-slate-500">{c.nome_fantasia}</div>}
                </td>
                <td className="px-4 py-3 text-slate-400">{c.cpf_cnpj || '-'}</td>
                <td className="px-4 py-3 text-slate-400">{c.telefone || '-'}</td>
                <td className="px-4 py-3 text-slate-400">{c.cidade ? `${c.cidade}/${c.uf || ''}` : '-'}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEditar(c)} className="text-slate-400 hover:text-white" data-testid={`financas-cliente-editar-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => excluir(c)} className="text-red-500 hover:text-red-400" data-testid={`financas-cliente-excluir-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-3xl w-[96vw] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Cadastro completo com todos os dados necessários para emissão de Nota Fiscal Eletrônica.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit}>
            <Tabs defaultValue="dados" className="w-full">
              <TabsList className="bg-slate-950 border border-white/5 grid grid-cols-3 w-full">
                <TabsTrigger value="dados" className="data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 text-xs">
                  <User className="w-3.5 h-3.5 mr-1" />Dados
                </TabsTrigger>
                <TabsTrigger value="endereco" className="data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 text-xs">
                  <MapPin className="w-3.5 h-3.5 mr-1" />Endereço
                </TabsTrigger>
                <TabsTrigger value="fiscal" className="data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 text-xs">
                  <FileText className="w-3.5 h-3.5 mr-1" />Fiscal
                </TabsTrigger>
              </TabsList>

              {/* DADOS */}
              <TabsContent value="dados" className="space-y-3 mt-4">
                <div>
                  <Label className="text-slate-400 text-xs">Tipo de Pessoa</Label>
                  <Select value={form.tipo_pessoa} onValueChange={(v) => setForm({ ...form, tipo_pessoa: v })}>
                    <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="financas-cliente-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10 text-white">
                      <SelectItem value="fisica">Pessoa Física (CPF)</SelectItem>
                      <SelectItem value="juridica">Pessoa Jurídica (CNPJ)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">{isPJ ? 'Razão Social *' : 'Nome Completo *'}</Label>
                  <Input data-testid="financas-cliente-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="bg-slate-950 border-white/10 text-white" required />
                </div>
                {isPJ && (
                  <div>
                    <Label className="text-slate-400 text-xs">Nome Fantasia</Label>
                    <Input value={form.nome_fantasia} onChange={(e) => setForm({ ...form, nome_fantasia: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs">{isPJ ? 'CNPJ' : 'CPF'}</Label>
                    <Input
                      data-testid="financas-cliente-doc"
                      value={form.cpf_cnpj}
                      onChange={(e) => setForm({ ...form, cpf_cnpj: isPJ ? maskCNPJ(e.target.value) : maskCPF(e.target.value) })}
                      placeholder={isPJ ? '00.000.000/0000-00' : '000.000.000-00'}
                      className="bg-slate-950 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">{isPJ ? 'Inscrição Municipal' : 'RG'}</Label>
                    <Input value={isPJ ? form.inscricao_municipal : form.rg} onChange={(e) => setForm(isPJ ? { ...form, inscricao_municipal: e.target.value } : { ...form, rg: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Telefone</Label>
                    <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: maskFone(e.target.value) })} placeholder="(11) 99999-9999" className="bg-slate-950 border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> E-mail</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="cliente@email.com" className="bg-slate-950 border-white/10 text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Observação</Label>
                  <Textarea value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} className="bg-slate-950 border-white/10 text-white" rows={2} />
                </div>
              </TabsContent>

              {/* ENDEREÇO */}
              <TabsContent value="endereco" className="space-y-3 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-1">
                    <Label className="text-slate-400 text-xs">CEP</Label>
                    <div className="flex gap-2">
                      <Input value={form.cep} onChange={(e) => setForm({ ...form, cep: maskCEP(e.target.value) })} placeholder="00000-000" className="bg-slate-950 border-white/10 text-white" data-testid="financas-cliente-cep" />
                      <Button type="button" onClick={buscarCep} disabled={loadingCep} size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white whitespace-nowrap">
                        {loadingCep ? '...' : 'Buscar'}
                      </Button>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-slate-400 text-xs">Logradouro</Label>
                    <Input value={form.logradouro} onChange={(e) => setForm({ ...form, logradouro: e.target.value })} placeholder="Rua, Avenida..." className="bg-slate-950 border-white/10 text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs">Número</Label>
                    <Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-slate-400 text-xs">Complemento</Label>
                    <Input value={form.complemento} onChange={(e) => setForm({ ...form, complemento: e.target.value })} placeholder="Apto, Sala..." className="bg-slate-950 border-white/10 text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Bairro</Label>
                  <Input value={form.bairro} onChange={(e) => setForm({ ...form, bairro: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-slate-400 text-xs">Cidade</Label>
                    <Input value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">UF</Label>
                    <Select value={form.uf} onValueChange={(v) => setForm({ ...form, uf: v })}>
                      <SelectTrigger className="bg-slate-950 border-white/10 text-white"><SelectValue placeholder="UF" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-white/10 text-white max-h-72">
                        {UFS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs">Código IBGE (cidade)</Label>
                    <Input value={form.codigo_ibge} onChange={(e) => setForm({ ...form, codigo_ibge: e.target.value })} placeholder="auto via CEP" className="bg-slate-950 border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">País</Label>
                    <Input value={form.pais} onChange={(e) => setForm({ ...form, pais: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
                  </div>
                </div>
              </TabsContent>

              {/* FISCAL */}
              <TabsContent value="fiscal" className="space-y-3 mt-4">
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 text-xs text-emerald-200/80">
                  Estes dados são exigidos para emissão de NF-e ao destinatário.
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Indicador de IE</Label>
                  <Select value={form.indicador_ie} onValueChange={(v) => setForm({ ...form, indicador_ie: v })}>
                    <SelectTrigger className="bg-slate-950 border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10 text-white">
                      <SelectItem value="1">1 - Contribuinte ICMS</SelectItem>
                      <SelectItem value="2">2 - Contribuinte Isento</SelectItem>
                      <SelectItem value="9">9 - Não Contribuinte (consumidor final)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs">Inscrição Estadual</Label>
                    <Input
                      value={form.inscricao_estadual}
                      onChange={(e) => setForm({ ...form, inscricao_estadual: e.target.value })}
                      placeholder={form.indicador_ie === '2' ? 'ISENTO' : 'apenas números'}
                      className="bg-slate-950 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Inscrição Municipal</Label>
                    <Input value={form.inscricao_municipal} onChange={(e) => setForm({ ...form, inscricao_municipal: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-4">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Cancelar</Button>
              <Button type="submit" data-testid="financas-cliente-salvar" className="bg-emerald-600 hover:bg-emerald-500 text-white">{editing ? 'Atualizar' : 'Cadastrar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
